import { UserState } from './../../../models/_core/user-state';
import { environment } from '../../../../environments/environment';
import { StorageService } from '../storage/storage.service';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError, from, firstValueFrom } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { NotificationsService } from '../notifications/notifications.service';
import { User } from 'src/app/models/user';

/**
 * ID: bh-interceptor-service
 * Name: BH Interceptor Service
 * Description: Service used to manage http requests and auth tokens
 * Version: 6
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2021-07-13 - MW - v2: Implemented userState
 * 2021-07-27 - MW - v3: Added BH-DONT-BUMP-INACTIVITY-TIMER header handling; will ignore timer bump
 * 2021-10-11 - MW - v4: Fixed issue with stored token session management
 * 2022-05-19 - MW - v5: Fixed persisten token issue
 * 2026-08-31 - v6: SSO refresh scoped to Learning Hub API requests; 401 retry with refresh
 */
@Injectable({
  providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {
  env = environment;
  private refreshInFlight: Promise<string | null> | null = null;

  constructor(
    private authService: AuthService,
    private storageService: StorageService,
    private notifications: NotificationsService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const stayOnPageWithError = request.headers.get('BH-STAY-ON-PAGE-WITH-ERROR') === 'true';
    request.headers.delete('BH-STAY-ON-PAGE-WITH-ERROR');

    const dontAssignHeaders = request.headers.get('BH-DONT-ASSIGN-HEADERS') === 'true';
    request.headers.delete('BH-DONT-ASSIGN-HEADERS');

    const isTokenRefresh = request.headers.get('BH-TOKEN-REFRESH') === 'true';
    request.headers.delete('BH-TOKEN-REFRESH');

    const dontBumpActivityTimer = request.headers.get('BH-DONT-BUMP-INACTIVITY-TIMER') === 'true';
    request.headers.delete('BH-DONT-BUMP-INACTIVITY-TIMER');

    if (this.env.requireTimeout && !dontBumpActivityTimer) {
      this.authService.bumpInactivityTimer();
    }

    const isLearningHubApi = this.isLearningHubApiRequest(request.url);

    return from(this.getAuthToken(request.url, isTokenRefresh, isLearningHubApi)).pipe(
      switchMap(token => {
        const clonedReq = this.addToken(request, token, dontAssignHeaders);
        let retriedAfterRefresh = false;
        return next.handle(clonedReq).pipe(
          map((event: HttpEvent<any>) => event),
          catchError(error => {
            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                if (
                  isLearningHubApi &&
                  !dontAssignHeaders &&
                  !isTokenRefresh &&
                  !retriedAfterRefresh &&
                  this.authService.isSsoAuthenticatedUser()
                ) {
                  retriedAfterRefresh = true;
                  return from(this.refreshToken()).pipe(
                    switchMap((newToken) => {
                      if (!newToken) {
                        if (!stayOnPageWithError) {
                          void this.authService.logout(true);
                        }
                        return throwError(() => error);
                      }
                      return next.handle(this.addToken(request, newToken, false));
                    })
                  );
                }
                if (!stayOnPageWithError) {
                  const authUser = this.authService.getAuthUser();
                  if (!this.authService.isDevMockUserToken(authUser?.token)) {
                    this.authService.logout(false);
                  }
                }
              } else {
                console.error('interceptor: error: ', error);
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  /** True for Learning Hub REST calls that should attach/refreshed SSO bearer tokens. */
  private isLearningHubApiRequest(url: string): boolean {
    return url.startsWith(this.env.apiUrl);
  }

  async getAuthToken(
    requestUrl: string,
    isTokenRefresh = false,
    isLearningHubApi = this.isLearningHubApiRequest(requestUrl)
  ): Promise<string | null> {
    this.notifications.suppressErrors = true;
    try {
      const authUser = await this.resolveAuthUserWithToken();
      const apiAccessToken = this.authService.getApiAccessToken();

      if (isLearningHubApi && !isTokenRefresh && this.authService.isSsoAuthenticatedUser(authUser)) {
        let cognitoToken = this.authService.resolveSsoCognitoBearer(authUser);
        const shouldRefresh =
          !cognitoToken ||
          this.authService.isTokenExpired(cognitoToken);
        if (shouldRefresh) {
          cognitoToken = await this.refreshToken();
        }
        if (cognitoToken) {
          return cognitoToken;
        }
      }

      let token: string | null = authUser?.token ?? null;
      const isDevMockToken = this.authService.isDevMockUserToken(token);
      if (!token || isDevMockToken) {
        token = apiAccessToken;
      }
      return token;
    } finally {
      this.notifications.suppressErrors = false;
    }
  }

  /** Merge in-memory auth user with stored token (SSO session restore). */
  private async resolveAuthUserWithToken(): Promise<User | undefined> {
    const authUser = this.authService.getAuthUser();
    const hasBearer =
      authUser?.token && !this.authService.isDevMockUserToken(authUser.token);
    if (hasBearer) {
      return authUser;
    }
    if (this.env.storeToken) {
      const userState = (await this.storageService.getData('userState', false)) as UserState;
      const stored = userState?.authUser;
      if (stored?.userId) {
        return {
          ...authUser,
          ...stored,
          token: stored.token ?? authUser?.token,
        };
      }
    }
    return authUser?.userId ? authUser : undefined;
  }

  async refreshToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.doRefreshToken();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async doRefreshToken(): Promise<string | null> {
    try {
      const refreshToken = await this.storageService.getData('refreshToken', true);
      if (!refreshToken || typeof refreshToken !== 'string') {
        console.warn('interceptor: refreshToken: no stored refresh token');
        return null;
      }
      const res = await firstValueFrom(this.authService.refreshSSOToken(refreshToken));
      return await this.authService.applySsoTokenRefresh(res);
    } catch (err: unknown) {
      console.error('interceptor: refreshToken: error: ', err);
      const oauthError =
        err instanceof HttpErrorResponse
          ? (err.error as { error?: string } | null)?.error
          : undefined;
      if (oauthError === 'invalid_grant') {
        void this.authService.logout(true);
      }
      return null;
    }
  }

  private addToken(request: HttpRequest<any>, token: string | null, dontAssignHeaders = false) {
    if (!dontAssignHeaders && token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }
    return request;
  }
}
